/**
 * S1 — Sign in. Nothing else on it.
 *
 * Branches on what `GET /auth/config` says, never on what the bundle assumes:
 *
 * - **`auth_mode: "dev"`** — a UPN field, behind the shim (§ 6.4).
 * - **`auth_mode: "entra"`** — the MSAL redirect flow (**built at D7**), or an
 *   honest account of why it cannot start.
 *
 * It is also where a *finished* session lands. A token that expired or was refused
 * puts a manager back here with the reason on screen — the server's words or
 * MSAL's, never a house sentence — because the failure this screen exists to
 * prevent is a dead screen that says nothing while a count sits half-entered.
 */

import { Link, Navigate } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import { DevSignInForm } from "../auth/DevSignInForm";
import { DEV_SHIM_COMPILED_IN } from "../auth/devShim";

export function SignIn() {
  const {
    config,
    configLoading,
    configError,
    authMode,
    devShimActive,
    entraReady,
    entraInitError,
    sessionMessage,
    signIn,
    signInPending,
    signedIn,
    displayName,
  } = useAuth();

  if (configLoading) {
    return <p className="muted">Reading sign-in configuration…</p>;
  }

  if (configError) {
    return (
      <section className="card error">
        <h2>Cannot reach the backend</h2>
        <p>
          <code>GET /auth/config</code> failed: {configError.message}
        </p>
        <p className="muted">
          The API is served from this same origin, so this usually means the
          backend process is down rather than a network problem. Diagnostics are
          on <a href="/about">/about</a>.
        </p>
      </section>
    );
  }

  // The build-time literal comes FIRST, deliberately: `DEV_SHIM_COMPILED_IN &&`
  // is what lets a production build fold this whole branch away and tree-shake
  // `DevSignInForm` out of the bundle. Reversing the operands would leave every
  // word of the dev form in the production JS — inert, but present, which is not
  // what § 6.4 asks for.
  if (DEV_SHIM_COMPILED_IN && devShimActive) {
    return <DevSignInForm />;
  }

  // Already signed in and looking at the sign-in screen — which happens when a
  // manager opens the app from the home screen, or when MSAL brings them back
  // here after a redirect. Send them on rather than making them tap again.
  if (signedIn) {
    return <Navigate to="/areas" replace />;
  }

  const registrationMissing = !config?.client_id;

  return (
    <section className="card">
      <h2>Sign in</h2>

      {/* The reason they are here, when there is one. First on the screen,
          because a manager who was mid-count needs to read this before anything
          else on the page. */}
      {sessionMessage && (
        <p className="signin-reason" role="alert" data-testid="session-message">
          <strong>Your session has ended.</strong> {sessionMessage}
        </p>
      )}

      {registrationMissing ? (
        <>
          <p>
            <strong>Sign-in is not available.</strong> The backend is in{" "}
            <code>entra</code> mode but <code>/auth/config</code> returned no client
            id, so there is nothing to initialise MSAL with.
          </p>
          <p className="muted">
            <code>AUTH_API_CLIENT_ID</code> has to be set in the VPS{" "}
            <code>.env</code> (phase 3 design § 6.3). That is a configuration
            question, not something this screen can retry.
          </p>
        </>
      ) : entraInitError ? (
        <>
          <p className="error">
            <strong>Sign-in could not be prepared.</strong> {entraInitError.message}
          </p>
          <p className="muted">
            The registration id and authority come from{" "}
            <code>/auth/config</code>, so this is a mismatch between this app and
            the backend rather than a wrong password. <a href="/about">Diagnostics</a>{" "}
            names both.
          </p>
        </>
      ) : (
        <>
          <p>
            Sign in with your <strong>@cubanbread.com</strong> staff account. Your
            count areas come from that identity.
          </p>
          <button
            type="button"
            onClick={signIn}
            disabled={!entraReady || signInPending}
            data-testid="entra-sign-in"
          >
            {signInPending ? "Taking you to Microsoft…" : "Sign in with Microsoft"}
          </button>
          {!entraReady && (
            <p className="muted small">Preparing sign-in…</p>
          )}
          <p className="muted small">
            Microsoft will ask for your password here, not this app — it never sees
            it. You will come back to this device when it is done.
          </p>
        </>
      )}

      {displayName && !signedIn && (
        <p className="muted small">
          Last signed in as {displayName}. <Link to="/areas">Your areas</Link>{" "}
          will load once you sign in again.
        </p>
      )}

      {DEV_SHIM_COMPILED_IN && (
        <p className="muted">
          This is a dev-mode bundle, but the server reports{" "}
          <code>auth_mode: {authMode}</code>, so the dev shim is refusing to act.
        </p>
      )}
    </section>
  );
}
